import { http } from '../request'
import type {
  SupportAutoReply,
  SupportAutoReplyCreateRequest,
  SupportAutoReplyListResponse,
  HomeBanner,
  HomeBannerListResponse,
  HomeBannerRequest,
  SupportAccountLinkCreateRequest,
  SupportAccountLinkSummary,
  SupportConversation,
  SupportConversationListResponse,
  SupportMessage,
  SupportMessageCreateRequest
} from '../types/support'

export const supportApi = {
  getActiveConversation(): Promise<SupportConversation> {
    return http.get('/api/services/support/conversations/active/')
  },

  getConversation(conversationId: number): Promise<SupportConversation> {
    return http.get(`/api/services/support/conversations/${conversationId}/`)
  },

  listMessages(conversationId: number): Promise<SupportMessage[]> {
    return http.get(`/api/services/support/conversations/${conversationId}/messages/`)
  },

  sendMessage(conversationId: number, data: SupportMessageCreateRequest): Promise<SupportMessage> {
    return http.post(`/api/services/support/conversations/${conversationId}/messages/send/`, data)
  },

  markRead(conversationId: number): Promise<{ success: boolean }> {
    return http.post(`/api/services/support/conversations/${conversationId}/mark-read/`)
  },

  requestHuman(conversationId: number): Promise<SupportConversation> {
    return http.post(`/api/services/support/conversations/${conversationId}/request-human/`)
  },

  matchAutoReplies(q: string): Promise<SupportAutoReply[]> {
    return http.get('/api/services/support/auto-replies/match/', { params: { q } })
  },

  listAutoReplies(params?: { page?: number; size?: number }): Promise<SupportAutoReplyListResponse> {
    return http.get('/api/services/support/auto-replies/', { params })
  },

  getAccountLinkSummary(accountId: number): Promise<SupportAccountLinkSummary> {
    return http.get(`/api/services/support/conversations/account-link/${accountId}/`)
  },

  listHomeBanners(params?: { page?: number; size?: number; display?: 'all' | 'desktop' | 'mobile' }): Promise<HomeBannerListResponse> {
    return http.get('/api/services/home-banners/', { params })
  }
}

export const adminSupportApi = {
  listConversations(params?: { page?: number; size?: number; status?: 'open' | 'closed'; assigned?: 'me' | 'unassigned' }): Promise<SupportConversationListResponse> {
    return http.get('/api/admin/services/support/conversations/', { params })
  },

  getConversation(conversationId: number): Promise<SupportConversation> {
    return http.get(`/api/admin/services/support/conversations/${conversationId}/`)
  },

  listMessages(conversationId: number): Promise<SupportMessage[]> {
    return http.get(`/api/admin/services/support/conversations/${conversationId}/messages/`)
  },

  sendMessage(conversationId: number, data: SupportMessageCreateRequest): Promise<SupportMessage> {
    return http.post(`/api/admin/services/support/conversations/${conversationId}/messages/send/`, data)
  },

  sendAccountLink(conversationId: number, data: SupportAccountLinkCreateRequest): Promise<SupportMessage> {
    return http.post(`/api/admin/services/support/conversations/${conversationId}/account-link/send/`, data)
  },

  markRead(conversationId: number): Promise<{ success: boolean }> {
    return http.post(`/api/admin/services/support/conversations/${conversationId}/mark-read/`)
  },

  getPendingDepositOrder(conversationId: number, accountId: number): Promise<Record<string, unknown>> {
    return http.get(`/api/admin/services/support/conversations/${conversationId}/pending-deposit-order/`, {
      params: { account_id: accountId }
    })
  },

  closeConversation(conversationId: number): Promise<SupportConversation> {
    return http.post(`/api/admin/services/support/conversations/${conversationId}/close/`)
  },

  reopenConversation(conversationId: number): Promise<SupportConversation> {
    return http.post(`/api/admin/services/support/conversations/${conversationId}/reopen/`)
  },

  assignMe(conversationId: number): Promise<SupportConversation> {
    return http.post(`/api/admin/services/support/conversations/${conversationId}/assign-me/`)
  },

  listAutoReplies(params?: { page?: number; size?: number; keyword?: string }): Promise<SupportAutoReplyListResponse> {
    return http.get('/api/admin/services/support/auto-replies/', { params })
  },

  createAutoReply(data: SupportAutoReplyCreateRequest): Promise<SupportAutoReply> {
    return http.post('/api/admin/services/support/auto-replies/', data)
  },

  updateAutoReply(id: number, data: SupportAutoReplyCreateRequest): Promise<SupportAutoReply> {
    return http.put(`/api/admin/services/support/auto-replies/${id}/`, data)
  },

  deleteAutoReply(id: number): Promise<{ success: boolean }> {
    return http.delete(`/api/admin/services/support/auto-replies/${id}/`)
  },

  listHomeBanners(params?: { page?: number; size?: number; keyword?: string; display?: 'all' | 'desktop' | 'mobile' }): Promise<HomeBannerListResponse> {
    return http.get('/api/admin/services/home-banners/', { params })
  },

  createHomeBanner(data: HomeBannerRequest): Promise<HomeBanner> {
    return http.post('/api/admin/services/home-banners/', data)
  },

  updateHomeBanner(id: number, data: HomeBannerRequest): Promise<HomeBanner> {
    return http.put(`/api/admin/services/home-banners/${id}/`, data)
  },

  deleteHomeBanner(id: number): Promise<{ success: boolean }> {
    return http.delete(`/api/admin/services/home-banners/${id}/`)
  }
}
